const CLEARABLE_PATTERNS = [
  /^.*-table-resizeColumnWidths$/,
  /^.*:columns$/,
  /^.*-table-selectedCellRange$/,
  /^sheet-filters-storage$/
];
const STORAGE_QUEUE_KEY = ":runtime-queue";
const temporaryStorageData = {};
const temporaryStorage = {
  getItem(key) {
    if (key in temporaryStorageData) {
      return temporaryStorageData[key];
    }
    return void 0;
  },
  removeItem(key) {
    delete temporaryStorageData[key];
  },
  setItem(key, value) {
    temporaryStorageData[key] = value;
  }
};
const getStorageEngine = () => {
  try {
    return typeof localStorage === "object" ? localStorage : temporaryStorage;
  } catch (error) {
    console.error(error);
  }
  return temporaryStorage;
};
let storageEngine = getStorageEngine();
const storage = (key) => {
  let clearAttempts = 0;
  const clearWithRetry = (dataToStore, setMethod) => {
    if (clearAttempts >= 10) {
      throw new Error(
        "Storage is too full, and maximum clear attempts exceeded."
      );
    }
    const itemCleared = clearStaleStorage();
    if (!itemCleared) {
      throw new Error("No clearable items available to free up storage.");
    }
    clearAttempts++;
    setMethod(dataToStore);
  };
  return {
    clear() {
      storageEngine.removeItem(key);
    },
    exists() {
      const data = storageEngine.getItem(key);
      return typeof data === "string" && data.length > 0;
    },
    getParsed() {
      try {
        const raw = this.getRaw();
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        throw new Error(e);
      }
    },
    getRaw() {
      const item = storageEngine.getItem(key);
      return item === null ? void 0 : item;
    },
    set(data) {
      let dataToStore = data;
      try {
        if (dataToStore === void 0) {
          return this.clear();
        }
        if (typeof dataToStore !== "string") {
          dataToStore = JSON.stringify(dataToStore);
        }
        storageEngine.setItem(key, dataToStore);
        updateRuntimeQueue(key);
      } catch (error) {
        if (error.name === "QuotaExceededError") {
          clearWithRetry(dataToStore, this.set.bind(this));
        } else {
          throw error;
        }
      }
    },
    setRaw(data) {
      try {
        if (data === void 0) {
          return this.clear();
        }
        storageEngine.setItem(key, data);
        updateRuntimeQueue(key);
      } catch (error) {
        if (error.name === "QuotaExceededError") {
          clearWithRetry(data, this.setRaw.bind(this));
        } else {
          throw error;
        }
      }
    },
    useTemporaryStorage() {
      storageEngine = temporaryStorage;
    }
  };
};
const getRuntimeQueue = () => {
  const rawQueue = storageEngine.getItem(STORAGE_QUEUE_KEY) ?? "[]";
  const storedQueue = JSON.parse(rawQueue);
  return new Set(storedQueue);
};
const updateRuntimeQueue = (key) => {
  if (key && isClearablePattern(key)) {
    const currentRuntimeQueue = getRuntimeQueue();
    if (currentRuntimeQueue.has(key)) {
      currentRuntimeQueue.delete(key);
    }
    currentRuntimeQueue.add(key);
    storageEngine.setItem(
      STORAGE_QUEUE_KEY,
      JSON.stringify(Array.from(currentRuntimeQueue))
    );
  }
};
const isClearablePattern = (key) => {
  return CLEARABLE_PATTERNS.some((pattern) => pattern.test(key));
};
const clearStaleStorage = () => {
  const currentRuntimeQueue = getRuntimeQueue();
  const firstKey = currentRuntimeQueue.values().next().value;
  if (firstKey) {
    storageEngine.removeItem(firstKey);
    currentRuntimeQueue.delete(firstKey);
    storageEngine.setItem(
      STORAGE_QUEUE_KEY,
      JSON.stringify(Array.from(currentRuntimeQueue))
    );
    return true;
  }
  return false;
};
export {
  storage
};
